import { masters } from "@nexseed/shared";
import { Cog } from "lucide-react";
import { createElement } from "react";
import { NextLabels } from "../components/next-labels";
import { defineMaster, type MasterRow } from "../spec";

/**
 * seedtrac's Admin "Resource Master" (manual p37-43): the entries the Processing slips pick from --
 * machine codes, drying floors and machines, and the pack sizes (UPS); operators have their own
 * master (user, 2026-10-27). One screen, one
 * table, told apart by Kind; a row with no plant serves every plant. Treatments have their own
 * master with crop/variety mappings. Drying floors and machines came back from the retired Drying
 * Unit master (user, 2026-10-27): a machine's trays, named like a warehouse's bins (A 20 kg,
 * B 20 kg), are what a Thermal batch ticks, and with every tray's kg Thermal suggests the No. of
 * batches.
 */
const LINE_TYPE_LABELS: Record<masters.DominoLineType, string> = {
  manual: "Manual",
  online: "Online (line printer + scale)",
};
const kindLabel = (k: unknown) =>
  masters.PROCESSING_RESOURCE_LABELS[k as masters.ProcessingResourceKind] ??
  String(k);
const traysOf = (r: MasterRow) =>
  (Array.isArray(r.trays) ? r.trays : []) as masters.DryingUnitTray[];
const kgText = (n: number) =>
  n.toLocaleString("en-IN", { maximumFractionDigits: 3 });

export const processingResources = defineMaster({
  key: "processing-resources",
  title: "Resource Master",
  singular: "Resource",
  legacyName: "Resource Master",
  description:
    "What the Processing, Packing, Drying and Thermal slips pick from: machine codes, drying floors and machines (with trays), pack sizes (UPS) and domino (pack) lines. Manage treatments in Treatment Schema Master and operators in Operator Master.",
  icon: Cog,
  schema: masters.processingResource.create,
  searchPlaceholder: "Search code or name",
  defaultSort: "code",
  listFilter: {
    param: "kind",
    label: "Kind",
    allLabel: "All kinds",
    options: masters.PROCESSING_RESOURCE_KINDS.map((k) => ({
      value: k,
      label: kindLabel(k),
    })),
  },
  optionLabel: (r: MasterRow) =>
    `${String(r.code)}${typeof r.name === "string" && r.name ? ` · ${r.name}` : ""}`,
  columns: [
    {
      id: "kind",
      header: "Kind",
      sortable: true,
      cell: (r: MasterRow) => kindLabel(r.kind),
    },
    { id: "code", header: "Code", kind: "mono", sortable: true },
    { id: "name", header: "Name" },
    { id: "grams", header: "Grams", kind: "number", align: "right" },
    // Root stock count sizes (docs/designs/root-stock-packing.md): seeds per packet.
    { id: "nos", header: "Nos", kind: "number", align: "right" },
    {
      id: "trays",
      header: "Trays / bins",
      cell: (r: MasterRow) =>
        traysOf(r)
          .map((t) =>
            t.capacityKg ? `${t.name} (${kgText(t.capacityKg)} kg)` : t.name
          )
          .join(", "),
    },
    {
      id: "lineType",
      header: "Line",
      cell: (r: MasterRow) =>
        r.lineType
          ? LINE_TYPE_LABELS[r.lineType as masters.DominoLineType]
          : "",
    },
    {
      id: "facilityId",
      header: "Plant",
      kind: "reference",
      reference: "facilities",
    },
    { id: "isActive", header: "Status", kind: "status" },
  ],
  fields: [
    {
      name: "kind",
      label: "Kind",
      type: "enum",
      required: true,
      options: masters.PROCESSING_RESOURCE_KINDS.filter(
        (k) => k !== "treatment"
      ).map((k) => ({ value: k, label: kindLabel(k) })),
      colSpan: 2,
    },
    {
      name: "code",
      label: "Code",
      type: "text",
      transform: "upper",
      required: true,
      description:
        "AB01, MG01, DR-01, OV1, 00004, 50G… One letter (A–J) for a domino line",
    },
    {
      name: "name",
      label: "Name",
      type: "text",
      description: "A description of the machine, floor, pack size or line",
    },
    {
      name: "grams",
      label: "Grams per pouch",
      type: "number",
      step: 0.001,
      min: 0,
      showIf: (v) => v.kind === "pack_size",
    },
    {
      name: "nos",
      label: "Nos per packet",
      type: "number",
      step: 1,
      min: 1,
      description:
        "Root stock only: seeds in one packet. Leave blank for a weight size; with it, Grams per pouch is the packet's standard weight",
      showIf: (v) => v.kind === "pack_size",
    },
    {
      name: "lineType",
      label: "Pack Line Type",
      type: "enum",
      required: true,
      options: masters.DOMINO_LINE_TYPES.map((t) => ({
        value: t,
        label: LINE_TYPE_LABELS[t],
      })),
      description:
        "Online lines push label text to the line printer and read the scale; Manual lines type the label range",
      showIf: (v) => v.kind === "domino_line",
    },
    {
      name: "trays",
      label: "Trays / bins",
      type: "drying-trays",
      required: true,
      colSpan: 2,
      description:
        "Name each tray (A, B…) and, if kept, the kg it holds. Thermal ticks them by name; with every tray's kg it suggests the No. of batches and warns when a batch is over",
      showIf: (v) => v.kind === "dryer_machine",
    },
    {
      name: "facilityId",
      label: "Plant",
      type: "reference",
      master: "facilities",
      nullable: true,
      description:
        "Leave blank to use it at every plant (a drying floor or machine needs its plant)",
      colSpan: 2,
    },
    { name: "isActive", label: "Active", type: "switch" },
  ],
  sheetSection: (row) =>
    row?.kind === "domino_line" ? createElement(NextLabels, { row }) : null,
});

export const treatmentSchemas = defineMaster({
  key: "treatment-schemas",
  title: "Treatment Schema Master",
  singular: "Treatment Schema",
  description:
    "Treatment colours and codes available in Processing for each crop or variety.",
  icon: Cog,
  schema: masters.treatmentSchema,
  searchPlaceholder: "Search code or colour",
  defaultSort: "code",
  optionLabel: (r: MasterRow) => `${String(r.code)} — ${String(r.colour)}`,
  columns: [
    { id: "code", header: "Code", kind: "mono", sortable: true },
    { id: "colour", header: "Colour", sortable: true },
    {
      id: "mappings",
      header: "Applicable crops / varieties",
      cell: (r: MasterRow) =>
        (
          (r.mappings as { cropName: string; varietyName: string | null }[]) ??
          []
        )
          .map((m) =>
            m.varietyName
              ? `${m.cropName}: ${m.varietyName}`
              : `${m.cropName} (all varieties)`
          )
          .join(", ") || "Mapping required",
    },
    {
      id: "facilityId",
      header: "Plant",
      kind: "reference",
      reference: "facilities",
    },
    { id: "isActive", header: "Status", kind: "status" },
  ],
  fields: [
    { name: "code", label: "Code", type: "text", required: true },
    { name: "colour", label: "Colour", type: "text", required: true },
    {
      name: "mappings",
      label: "Applicable crops / varieties",
      type: "treatment-mappings",
      required: true,
      colSpan: 2,
      description:
        "Leave variety blank for all varieties. Crop and specific variety choices are combined.",
    },
    {
      name: "facilityId",
      label: "Plant",
      type: "reference",
      master: "facilities",
      nullable: true,
      description: "Leave blank for all plants",
    },
    { name: "isActive", label: "Active", type: "switch" },
  ],
});
